import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, Injector, NgZone } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import {
  AUTH_STORAGE_KEY,
  clearLegacyPendingSessionExpiry,
  ensureSessionIdleState,
  getSessionIdleDeadlineMs,
  hasActivitySinceTokenReceived,
  isSessionIdleExpired,
  parseSessionExpiryRedirectMarker,
  readStoredAuthSession,
  SESSION_EXPIRY_REDIRECT_QUERY_PARAM,
  withSessionActivity,
  writeStoredAuthSession
} from '@knowledgehook/auth-storage';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { APP_ROUTE } from 'app/app-routing.constant';
import { AuthenticationDataService, AuthenticationService, UserRole } from 'app/core/authentication';
import { TOKEN_STORAGE_KEYS } from 'app/core/authentication/constants/token-storage-keys';
import { LoginService } from 'app/core/authentication/services/login.service';
import { ModalService } from 'app/core/modals/services/modal.service';
import { Themes } from 'app/core/themes/themes.enum';
import { ThemesService } from 'app/core/themes/themes.service';
import { DataTrackLabelEnum } from 'app/shared/enums/data-track-label.enum';
import { KhAnalyticsService } from 'app/shared/services/kh-analytics.service';
import { SessionExpiredModalComponent } from '../session-expired-modal/session-expired-modal.component';

import { SessionRefreshApiService } from './session-refresh-api.service';

@Injectable({
  providedIn: 'root'
})
export class SessionRefreshCoordinatorService {
  private static readonly SESSION_EXPIRED_ERROR = 'session_expired';
  private static readonly MAX_REFRESH_CHECK_INTERVAL_MS = 55 * 60 * 1000;
  private static readonly MAX_REFRESH_BUFFER_MS = 5 * 60 * 1000;
  private static readonly MIN_REFRESH_BUFFER_MS = 1000;
  private static readonly MIN_REFRESH_CHECK_DELAY_MS = 1000;
  private static readonly REFRESH_BUFFER_RATIO = 1 / 12;
  private static readonly SESSION_TIMEOUT_CLICK_ANALYTICS_WAIT_MS = 1000;
  private static readonly ACTIVITY_STORAGE_THROTTLE_MS = 1000;
  private static readonly MAX_IDLE_CHECK_INTERVAL_MS = 55 * 60 * 1000;

  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly authenticationDataService = inject(AuthenticationDataService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly loginService = inject(LoginService);
  private readonly modalService = inject(ModalService);
  private readonly themesService = inject(ThemesService);
  private readonly analyticsService = inject(KhAnalyticsService);

  private activitySubscription?: Subscription;
  private eventCleanups: (() => void)[] = [];
  private lastActivityAtMs: number | null = null;
  private lastPersistedActivityAtMs: number | null = null;
  private isRefreshing = false;
  private isRunning = false;
  private isSessionExpiryHandling = false;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshSubscription?: Subscription;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private skippedRefresh = false;
  private capturedIsStudent = false;

  public start(): void {
    if (!this.authenticationDataService.getAccessToken()) {
      return;
    }

    if (!this.isStoredSessionValid()) {
      return;
    }

    this.capturedIsStudent = this.readStudentRoleFromClaims();
    this.initializeIdleState();
    this.isSessionExpiryHandling = false;

    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.bindQualifyingActivity();
    this.scheduleIdleCheck();
    this.scheduleNextRefreshCheck();
  }

  public stop(): void {
    this.clearIdleTimer();
    this.clearRefreshTimer();
    this.activitySubscription?.unsubscribe();
    this.activitySubscription = undefined;
    this.refreshSubscription?.unsubscribe();
    this.refreshSubscription = undefined;
    this.eventCleanups.forEach((cleanup) => { cleanup(); });
    this.eventCleanups = [];
    this.lastActivityAtMs = null;
    this.lastPersistedActivityAtMs = null;
    this.isRefreshing = false;
    this.isRunning = false;
    this.skippedRefresh = false;
  }

  public handlePendingSessionExpiry(): boolean {
    clearLegacyPendingSessionExpiry();
    const redirectMarker = this.consumeSessionExpiryRedirectMarker();

    if (!redirectMarker) {
      return false;
    }

    this.isSessionExpiryHandling = true;
    this.stop();
    this.showSessionExpiredModal(false);

    return true;
  }

  public recordQualifyingActivity(): void {
    if (!this.isRunning || !this.authenticationDataService.getAccessToken()) {
      return;
    }

    // WHY: Activity after the idle deadline must not revive a JWT which is still technically
    // valid. Check the previous deadline before moving the timestamp.
    if (!this.isStoredSessionValid()) {
      this.handleSessionExpired();
      return;
    }

    const nowMs = Date.now();
    this.lastActivityAtMs = Math.max(this.lastActivityAtMs ?? 0, nowMs);
    this.persistActivityIfNeeded(nowMs);
    this.scheduleIdleCheck();

    if (!this.skippedRefresh) {
      return;
    }

    if (this.isStoredSessionValid()) {
      this.refreshNow();
    }
    else {
      this.handleSessionExpired();
    }
  }

  public handleSessionExpired(failedRequestToken?: string | null): boolean {
    const latestToken = this.authenticationDataService.getAccessToken();
    const storedSessionValid = this.isStoredSessionValid();

    if (latestToken
      && latestToken !== failedRequestToken
      && storedSessionValid) {
      return false;
    }

    if (this.isSessionExpiryHandling) {
      return true;
    }

    this.isSessionExpiryHandling = true;
    this.stop();
    this.showSessionExpiredModal();

    return true;
  }

  private bindQualifyingActivity(): void {
    this.ngZone.runOutsideAngular(() => {
      this.eventCleanups = [
        this.bindDocumentEvent('keydown', (event) => {
          if (this.isInputActivityTarget(event.target)) {
            this.runRecordQualifyingActivity();
          }
        }),
        this.bindDocumentEvent('submit', () => { this.runRecordQualifyingActivity(); }),
        this.bindDocumentEvent('pointerdown', () => { this.runRecordQualifyingActivity(); }),
        this.bindDocumentEvent('visibilitychange', () => {
          if (this.document.visibilityState === 'visible') {
            this.runCheckIdleDeadline();
          }
        }),
        this.bindWindowStorageEvent()
      ];
    });

    this.activitySubscription = this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe(() => { this.recordQualifyingActivity(); });
  }

  private bindDocumentEvent(
    eventName: 'keydown' | 'pointerdown' | 'submit' | 'visibilitychange',
    handler: (event: Event) => void): () => void {
    this.document.addEventListener(eventName, handler, true);

    return () => {
      this.document.removeEventListener(eventName, handler, true);
    };
  }

  private isInputActivityTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return target instanceof HTMLInputElement
      || target instanceof HTMLTextAreaElement
      || target instanceof HTMLSelectElement
      || target.isContentEditable;
  }

  private bindWindowStorageEvent(): () => void {
    const window = this.document.defaultView;
    if (!window) {
      return () => {};
    }

    const handler = (event: StorageEvent) => {
      if (event.key !== AUTH_STORAGE_KEY) {
        return;
      }

      this.initializeIdleState();
      this.runCheckIdleDeadline();
    };
    window.addEventListener('storage', handler);

    return () => { window.removeEventListener('storage', handler); };
  }

  private runRecordQualifyingActivity(): void {
    this.ngZone.run(() => {
      this.recordQualifyingActivity();
    });
  }

  private scheduleNextRefreshCheck(): void {
    this.clearRefreshTimer();
    const delayMs = this.getNextRefreshCheckDelayMs();

    this.ngZone.runOutsideAngular(() => {
      this.refreshTimer = setTimeout(() => {
        this.ngZone.run(() => { this.runRefreshCheck(); });
      }, delayMs);
    });
  }

  private runRefreshCheck(): void {
    if (!this.isRunning) {
      return;
    }

    if (!this.isStoredSessionValid()) {
      this.handleSessionExpired();
      return;
    }

    if (!this.hasActivitySinceCurrentToken()) {
      this.skippedRefresh = true;
      this.scheduleNextRefreshCheck();

      return;
    }

    this.refreshNow();
  }

  private refreshNow(): void {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;
    this.clearRefreshTimer();
    this.persistCurrentActivity();
    const refreshRequestToken = this.authenticationDataService.getAccessToken();

    this.refreshSubscription = this.injector.get(SessionRefreshApiService).refreshToken()
      .subscribe({
        next: (tokenResponse) => {
          if (!this.isRunning) {
            return;
          }

          const latestToken = this.authenticationDataService.getAccessToken();
          if (latestToken !== refreshRequestToken && this.isStoredSessionValid()) {
            this.isRefreshing = false;
            this.initializeIdleState();
            this.scheduleIdleCheck();
            this.scheduleNextRefreshCheck();
            return;
          }

          this.persistCurrentActivity();
          if (!this.isStoredSessionValid()) {
            this.handleSessionExpired(refreshRequestToken);
            return;
          }

          this.loginService.setTokenResponse(tokenResponse, true);
          this.isRefreshing = false;
          this.skippedRefresh = false;
          this.initializeIdleState();
          this.scheduleIdleCheck();
          this.scheduleNextRefreshCheck();
        },
        error: (error) => {
          this.isRefreshing = false;

          if (this.isSessionExpiredError(error)) {
            const didExpireSession = this.handleSessionExpired(refreshRequestToken);

            if (!didExpireSession) {
              this.skippedRefresh = false;
              this.scheduleNextRefreshCheck();
            }

            return;
          }

          this.scheduleNextRefreshCheck();
        }
      });
  }

  private isSessionExpiredError(error: unknown): boolean {
    return error instanceof HttpErrorResponse
      && error.status === 401
      && error.error?.error === SessionRefreshCoordinatorService.SESSION_EXPIRED_ERROR;
  }

  private isStoredSessionValid(): boolean {
    const session = this.getEffectiveStoredSession();
    if (!session) {
      return false;
    }

    const expiryMs = Date.parse(session.expiry);
    return Number.isFinite(expiryMs)
      && Date.now() < expiryMs
      && !isSessionIdleExpired(session);
  }

  private initializeIdleState(): void {
    const storedSession = readStoredAuthSession();
    if (!storedSession) {
      this.lastActivityAtMs = null;
      this.lastPersistedActivityAtMs = null;
      return;
    }

    const session = ensureSessionIdleState(storedSession);
    if (session !== storedSession) {
      writeStoredAuthSession(session);
    }

    const storedActivityAtMs = Date.parse(session.lastActivityAt);
    this.lastActivityAtMs = Math.max(this.lastActivityAtMs ?? 0, storedActivityAtMs);
    this.lastPersistedActivityAtMs = storedActivityAtMs;
  }

  private persistActivityIfNeeded(activityAtMs: number): void {
    if (activityAtMs - (this.lastPersistedActivityAtMs ?? 0) <
      SessionRefreshCoordinatorService.ACTIVITY_STORAGE_THROTTLE_MS) {
      return;
    }

    const session = readStoredAuthSession();
    if (!session) {
      return;
    }

    writeStoredAuthSession(withSessionActivity(session, activityAtMs));
    this.lastPersistedActivityAtMs = activityAtMs;
  }

  private persistCurrentActivity(): void {
    if (this.lastActivityAtMs === null
      || this.lastActivityAtMs <= (this.lastPersistedActivityAtMs ?? 0)) {
      return;
    }

    const session = readStoredAuthSession();
    if (!session) {
      return;
    }

    writeStoredAuthSession(withSessionActivity(session, this.lastActivityAtMs));
    this.lastPersistedActivityAtMs = this.lastActivityAtMs;
  }

  private getEffectiveStoredSession() {
    const storedSession = readStoredAuthSession();
    if (!storedSession) {
      return null;
    }

    const session = ensureSessionIdleState(storedSession);
    if ((this.lastActivityAtMs ?? 0) <= Date.parse(session.lastActivityAt)) {
      return session;
    }

    return {
      ...session,
      lastActivityAt: new Date(this.lastActivityAtMs as number).toISOString()
    };
  }

  private hasActivitySinceCurrentToken(): boolean {
    const session = this.getEffectiveStoredSession();
    return !!session && hasActivitySinceTokenReceived(session);
  }

  private scheduleIdleCheck(): void {
    this.clearIdleTimer();
    const session = this.getEffectiveStoredSession();
    if (!session) {
      return;
    }

    const deadlineMs = getSessionIdleDeadlineMs(session);
    if (deadlineMs === null) {
      return;
    }

    const delayMs = Math.max(0, Math.min(
      deadlineMs - Date.now(),
      SessionRefreshCoordinatorService.MAX_IDLE_CHECK_INTERVAL_MS));

    this.ngZone.runOutsideAngular(() => {
      this.idleTimer = setTimeout(() => {
        this.ngZone.run(() => { this.runCheckIdleDeadline(); });
      }, delayMs);
    });
  }

  private runCheckIdleDeadline(): void {
    if (!this.isRunning) {
      return;
    }

    if (!this.isStoredSessionValid()) {
      this.handleSessionExpired();
      return;
    }

    this.scheduleIdleCheck();
  }

  private clearIdleTimer(): void {
    if (this.idleTimer === null) {
      return;
    }

    clearTimeout(this.idleTimer);
    this.idleTimer = null;
  }

  private showSessionExpiredModal(isStudent?: boolean): void {
    const useStudentSkin = isStudent ?? this.isStudentSkin();
    const redirectUrl = this.getSessionExpiredRedirectUrl(isStudent ?? this.capturedIsStudent);
    this.analyticsService.trackEventSimple(DataTrackLabelEnum.SessionTimeoutSeen);

    this.modalService.displayCustomModal<SessionExpiredModalComponent>(SessionExpiredModalComponent, {
      backdrop: 'static',
      className: useStudentSkin
        ? 'modal-dialog-centered modal-md'
        : 'modal-dialog-centered friendly-modal modal-sm-plus',
      data: {
        ctaHandler: async () => { await this.onSessionExpiredCtaClick(redirectUrl); },
        isStudent: useStudentSkin
      },
      ignoreBackdropClick: true,
      keyboard: false
    });
  }

  private async onSessionExpiredCtaClick(redirectUrl: string[]): Promise<void> {
    await Promise.race([
      this.analyticsService.trackEventSimpleAsync(DataTrackLabelEnum.SessionTimeoutClicked),
      this.waitForSessionTimeoutAnalytics()
    ]);

    localStorage.setItem(TOKEN_STORAGE_KEYS.POST_LOGOUT_REDIRECT, JSON.stringify({
      params: {},
      reload: true,
      url: redirectUrl
    }));
    // Pass samlRedirect=false: a session timeout should clear auth + route without the
    // SAML single-logout iframe, which bootstraps the old AngularJS app.
    this.authenticationService.logOut(false);
  }

  private waitForSessionTimeoutAnalytics(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, SessionRefreshCoordinatorService.SESSION_TIMEOUT_CLICK_ANALYTICS_WAIT_MS);
    });
  }

  private getSessionExpiredRedirectUrl(isStudent: boolean): string[] {
    if (isStudent) {
      return APP_ROUTE.STUDENT.JOIN_CODE.route();
    }

    return APP_ROUTE.LOGIN.route();
  }

  private isStudentSkin(): boolean {
    const theme = this.themesService.getCurrentTheme();
    if (theme === Themes.SciFi) {
      return true;
    }

    if (theme === Themes.Friendly) {
      return false;
    }

    return this.capturedIsStudent;
  }

  private readStudentRoleFromClaims(): boolean {
    const roles = this.authenticationDataService.getUserClaims()?.roles ?? [];
    return roles.includes(UserRole.student);
  }

  private consumeSessionExpiryRedirectMarker(): boolean {
    const url = new URL(window.location.href);
    const marker = url.searchParams.get(SESSION_EXPIRY_REDIRECT_QUERY_PARAM);

    if (marker === null) {
      return false;
    }

    url.searchParams.delete(SESSION_EXPIRY_REDIRECT_QUERY_PARAM);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);

    return parseSessionExpiryRedirectMarker(marker);
  }

  private getNextRefreshCheckDelayMs(): number {
    const expiry = this.authenticationDataService.getUserClaims()?.expiry;
    if (!expiry) {
      return SessionRefreshCoordinatorService.MAX_REFRESH_CHECK_INTERVAL_MS;
    }

    const msUntilExpiry = expiry.diff(DateTime.utc(), 'milliseconds').milliseconds;
    if (msUntilExpiry <= 0) {
      return 0;
    }

    const bufferMs = Math.min(
      SessionRefreshCoordinatorService.MAX_REFRESH_BUFFER_MS,
      Math.max(
        SessionRefreshCoordinatorService.MIN_REFRESH_BUFFER_MS,
        msUntilExpiry * SessionRefreshCoordinatorService.REFRESH_BUFFER_RATIO
      )
    );
    const delayMs = Math.min(
      SessionRefreshCoordinatorService.MAX_REFRESH_CHECK_INTERVAL_MS,
      msUntilExpiry - bufferMs
    );

    return Math.max(SessionRefreshCoordinatorService.MIN_REFRESH_CHECK_DELAY_MS, delayMs);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer === null) {
      return;
    }

    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }
}
